'use client';

import { useQuery } from '@tanstack/react-query';

import { FriendCardType, RelationshipStatus } from '@/modules/players/v3/types/friend';
import { getRelationship } from '@/utils/actions/players/friends/getRelationship';

export const RELATIONSHIP_QUERY_KEY = 'relationship';

export interface UseGetRelationshipParams {
  userUuid: string | undefined;
  targetUuid: string | undefined;
}

export interface RelationshipState {
  relationships: RelationshipStatus[];
  friendType: FriendCardType;
  isFollowing: boolean;
  isFollower: boolean;
  isBlocked: boolean;
}

// Friend and follow are independent modules — the array carries every relation at once,
// so each axis is derived on its own instead of collapsing to a single status.
const deriveFriendType = (relationships: RelationshipStatus[]): FriendCardType => {
  if (relationships.includes(RelationshipStatus.BLOCKED)) return FriendCardType.BLOCKED;
  if (relationships.includes(RelationshipStatus.FRIENDS)) return FriendCardType.FRIEND;
  if (relationships.includes(RelationshipStatus.REQUEST_RECEIVED)) return FriendCardType.REQUEST;
  if (relationships.includes(RelationshipStatus.REQUEST_SENT)) return FriendCardType.PENDING;
  return FriendCardType.SUGGESTION;
};

const toRelationshipState = (relationships: RelationshipStatus[]): RelationshipState => ({
  relationships,
  friendType: deriveFriendType(relationships),
  isFollowing: relationships.includes(RelationshipStatus.FOLLOWING),
  isFollower: relationships.includes(RelationshipStatus.FOLLOWER),
  isBlocked: relationships.includes(RelationshipStatus.BLOCKED),
});

export const useGetRelationship = ({ userUuid, targetUuid }: UseGetRelationshipParams) => {
  return useQuery<RelationshipState>({
    queryKey: [RELATIONSHIP_QUERY_KEY, userUuid, targetUuid],
    queryFn: async () => {
      const res = await getRelationship({ userUuid: userUuid!, targetUuid: targetUuid! });
      if (res.error || !res.data) {
        return toRelationshipState([]);
      }
      return toRelationshipState(res.data);
    },
    enabled: !!userUuid && !!targetUuid,
  });
};
